/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Grid,
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { LightPurpleButton } from "../components/_default/buttonStyles";
import { loginUser } from "../_redux/userRelated/userHandle";
import Popup from "../components/_default/Popup";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import siteData from "../_data";
import Layout1 from "../components/_layout/_layout_1";
import { useScreenSize } from "../components/_context/ScreenSizeProvider";

// Configuration for form fields based on role
const formConfig = {
  Admin: {
    fields: [
      {
        name: "email",
        label: "Enter your email",
        type: "email",
        autoComplete: "email",
        required: true,
        validation: Yup.string()
          .email("Invalid email format")
          .required("Email is required"),
      },
      {
        name: "password",
        label: "Password",
        type: "password",
        autoComplete: "current-password",
        required: true,
        validation: Yup.string().required("Password is required"),
      },
    ],
    signupLink: "/Adminregister",
  },
  Student: {
    fields: [
      {
        name: "rollNumber",
        label: "Enter your Roll Number",
        type: "number",
        autoComplete: "off",
        required: true,
        validation: Yup.number()
          .typeError("Roll Number must be a number")
          .required("Roll Number is required"),
      },
      {
        name: "studentName",
        label: "Enter your name",
        type: "text",
        autoComplete: "name",
        required: true,
        validation: Yup.string().required("Name is required"),
      },
      {
        name: "password",
        label: "Password",
        type: "password",
        autoComplete: "current-password",
        required: true,
        validation: Yup.string().required("Password is required"),
      },
    ],
    signupLink: null, // Assuming Students don't have a signup link
  },
  Teacher: {
    fields: [
      {
        name: "email",
        label: "Enter your email",
        type: "email",
        autoComplete: "email",
        required: true,
        validation: Yup.string()
          .email("Invalid email format")
          .required("Email is required"),
      },
      {
        name: "password",
        label: "Password",
        type: "password",
        autoComplete: "current-password",
        required: true,
        validation: Yup.string().required("Password is required"),
      },
    ],
    signupLink: null, // Assuming Teachers don't have a signup link
  },
};

// Styled Link using MUI's styled API
const StyledLink = styled(RouterLink)(({ theme }) => ({
  marginTop: theme.spacing(1),
  textDecoration: "none",
  color: "#7f56da",
  "&:hover": {
    textDecoration: "underline",
  },
}));

const { brand } = siteData;

const LoginPage = ({ role }) => (
  <Layout1>
    <LoginForm role={role} />
  </Layout1>
);

const LoginForm = ({ role }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { width } = useScreenSize();
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const { status, currentUser, response, currentRole } = useSelector(
    (state) => state.user
  );

  const [togglePassword, setTogglePassword] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [message, setMessage] = useState("");

  // Extract form fields and signup link based on role
  const { fields, signupLink } = formConfig[role] || {};

  // Generate validation schema dynamically
  const validationSchema = useMemo(() => {
    const schemaFields = fields.reduce((acc, field) => {
      acc[field.name] = field.validation;
      return acc;
    }, {});
    return Yup.object().shape(schemaFields);
  }, [fields]);

  // Generate initial values dynamically
  const initialValues = useMemo(() => {
    const initial = fields.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, {});
    initial.remember = false;
    return initial;
  }, [fields]);

  // Handle form submission
  const handleSubmit = (values, { setSubmitting }) => {
    const { remember, ...loginFields } = values;

    dispatch(loginUser(loginFields, role));
    setSubmitting(false);
  };

  // Handle side effects based on authentication status
  useEffect(() => {
    if (status === "success" || currentUser) {
      const dashboardPath = `/${currentRole}/dashboard`;
      navigate(dashboardPath);
    } else if (status === "failed") {
      setMessage(response);
      setShowPopup(true);
    } else if (status === "error") {
      setMessage("Network Error");
      setShowPopup(true);
    }
  }, [status, currentRole, navigate, response, currentUser]);

  // Calculate form width once
  const formWidth = useMemo(() => {
    const baseWidth = isMobileView ? width : width / 2;
    return baseWidth - baseWidth * 0.2; // 80% of baseWidth
  }, [isMobileView, width]);

  return (
    <>
      <Box
        display="flex"
        py={3}
        flexDirection={{ lg: "row", md: "column", sm: "column", xs: "column" }}
        justifyContent="center"
        alignItems="stretch"
        sx={{ width: `${width}px` }}
      >
        <Box
          flex="1"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          py={2}
          px={{ xxl: 5, xl: 4, lg: 3, md: 2, sm: 2, xs: 2 }}
        >
          <Typography variant="h4" mb={2} color="#2c2143">
            {role} Login
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Welcome back! Please enter your details
          </Typography>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
              <Form
                noValidate
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {fields.map((field) => (
                  <TextField
                    key={field.name}
                    margin="normal"
                    required={field.required}
                    fullWidth
                    id={field.name}
                    label={field.label}
                    name={field.name}
                    type={field.type === "password" && togglePassword ? "text" : field.type}
                    autoComplete={field.autoComplete}
                    autoFocus={field.name === fields[0]?.name}
                    value={values[field.name]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched[field.name] && Boolean(errors[field.name])}
                    helperText={touched[field.name] && errors[field.name]}
                    InputProps={
                      field.name === "password"
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setTogglePassword((prev) => !prev)}
                                  edge="end"
                                >
                                  {togglePassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        : {}
                    }
                    sx={{ width: formWidth }}
                  />
                ))}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width={formWidth}
                  mt={1}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="remember"
                        color="primary"
                        checked={values.remember}
                        onChange={handleChange}
                      />
                    }
                    label="Remember me"
                  />
                  <StyledLink to="/forgot-password">Forgot password?</StyledLink>
                </Box>
                <LightPurpleButton
                  type="submit"
                  variant="contained"
                  sx={{ mt: 3, width: formWidth }}
                  disabled={isSubmitting || status === "loading"}
                >
                  {status === "loading" ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Login"
                  )}
                </LightPurpleButton>

                {signupLink && (
                  <Grid container justifyContent="center" alignItems="center" mt={2} width={formWidth}>
                    <Grid item>
                      <Typography variant="body2">Don't have an account?</Typography>
                    </Grid>
                    <Grid item>
                      <StyledLink to={signupLink} sx={{ ml: 1 }}>
                        Sign up
                      </StyledLink>
                    </Grid>
                  </Grid>
                )}
              </Form>
            )}
          </Formik>
        </Box>

        {!isMobileView && (
          <Box
            flex="1"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={2}
          >
            <Box
              sx={{
                width: 300,
                height: 300,
                backgroundImage: `url(${brand.logoRemovebgSrc})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
                backgroundPosition: "center",
              }}
            />
          </Box>
        )}
      </Box>
      <Popup
        message={message}
        setShowPopup={setShowPopup}
        showPopup={showPopup}
      />
    </>
  );
};

export default LoginPage;
